import { Center, Group, MultiSelect, Pagination, ScrollArea } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { ErrorBoundary } from '@sentry/react';
import { useGetDebtorTimeline } from 'api/index';
import { type CleanActivity, TimelineTypes } from 'api/models';
import { ActivityItem } from 'app/Private/Clients/Detail/ActivitiesTab/ActivityItem';
import { ActivityItemSkeleton } from 'app/Private/Clients/Detail/ActivitiesTab/ActivityItemSkeleton';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { FixAppHeight } from 'components/FixAppHeight';
import { Panel } from 'components/Panel';
import { tKeys, useTranslation } from 'locales/index';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { isDefined, isEmpty, isNonNullish } from 'remeda';
import type { StoreState } from 'types/storeTypes';
import { ActivitiesEmptyState } from './ActivitiesEmptyState';
import { ActivitiesNoResults } from './ActivitiesNoResults';

const ActivitiesTabInner = () => {
  const debtor = useDebtor();
  const { t } = useTranslation();
  const filters = useForm();
  const [page, setPage] = useState(1);
  const { data: debtorTimelineResponse, isLoading: isActivitiesLoading } = useGetDebtorTimeline(
    debtor.id,
    {
      page_limit: 20,
      page: page,
      types: filters.values.types,
      category: filters.values.category,
      start_date: filters.values.dateRange?.[0],
      end_date: filters.values.dateRange?.[1],
    },
    {
      query: {
        enabled: isNonNullish(debtor.id),
        placeholderData: previousData => {
          if (isDefined(previousData)) return previousData;
        },
      },
    },
  );

  const values = filters.getValues();
  const areDatesDefined =
    isNonNullish(values.dateRange?.[0]) || isNonNullish(values.dateRange?.[1]);
  const areTypesPopulated = isNonNullish(values.types) && !isEmpty(values.types);
  const areValuesPopulated = isNonNullish(values.category) && !isEmpty(values.category);
  const areFiltersPopulated = areValuesPopulated || areDatesDefined || areTypesPopulated;

  const { activity_categories } = useSelector((state: StoreState) => state.app.constants);
  const categories = activity_categories.map(({ value, description }) => ({
    label: description,
    value,
  }));

  const types = Object.values(TimelineTypes).map(type => ({
    value: type,
    label: t((tKeys.TIMELINE as any)[type.toUpperCase()]),
  }));

  const activities = debtorTimelineResponse?.data ?? [];

  if (isActivitiesLoading) return <LoadingState />;
  if (isEmpty(activities) && !areFiltersPopulated) return <ActivitiesEmptyState />;

  return (
    <>
      <FixAppHeight pb={60} />
      <Group mt="lg" mb="lg">
        <DatePickerInput
          clearable
          type="range"
          placeholder={t(tKeys.FORM.DATEPICKER_RANGE)}
          {...filters.getInputProps('dateRange')}
        />
        <MultiSelect
          clearable
          placeholder={t(tKeys.FORM.TYPE)}
          data={types}
          {...filters.getInputProps('types')}
        />
        <MultiSelect
          clearable
          placeholder={t(tKeys.FORM.CATEGORY)}
          data={categories}
          {...filters.getInputProps('category')}
        />
      </Group>
      {isEmpty(activities) && areFiltersPopulated && <ActivitiesNoResults />}
      <Panel flushContent mah="calc(100% - 190px)">
        <ScrollArea.Autosize type="hover">
          {activities.map(activity => (
            <ActivityItem key={activity.id} activity={activity as CleanActivity} />
          ))}
        </ScrollArea.Autosize>
      </Panel>
      <Center m="lg">
        <Pagination
          total={debtorTimelineResponse?.metadata.pagination.last_page ?? 1}
          value={page}
          onChange={setPage}
        />
      </Center>
    </>
  );
};

export const ActivitiesTab = () => {
  return (
    <ErrorBoundary fallback={<div>{'An error occured while loading this page'}</div>}>
      <ActivitiesTabInner />
    </ErrorBoundary>
  );
};

const LoadingState = () => {
  return (
    <>
      <Panel mt="xl" py={20} pt={4} mah="calc(100% - 190px)">
        <ActivityItemSkeleton />
        <ActivityItemSkeleton />
        <ActivityItemSkeleton />
      </Panel>
      <Center m="lg">
        <Pagination total={3} />
      </Center>
    </>
  );
};
